import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './ReferralFlows.styled';
import ReferralAnalysesList from 'components/Analyses/ReferralAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useReferralFlows from 'hooks/useReferralFlows';
import useReferralAnalyses from 'hooks/useReferralAnalyses';
import { DELETE_REFERRAL_FLOW } from 'helpers/confirmations';
import { needToUpdateField, formatDuration, parseDuration } from 'helpers';
import { timezones } from 'helpers/constants';
import { referralFlowsSelectors } from "features/referralFlows";

const weekdays = {
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false
};

const ReferralFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getReferralFlow, createReferralFlow, updateReferralFlow } = useReferralFlows();
  const { getReferralAnalyses, clearLocalReferralAnalyses } = useReferralAnalyses();
  const id = useCustomSelector(referralFlowsSelectors.getReferralFlowId);
  const referralFlow = useCustomSelector(referralFlowsSelectors.getReferralFlowData);
  const fetching = useCustomSelector(referralFlowsSelectors.referralFlowIsFetching);
  const [days, setDays] = useState(weekdays);
  const [error, setError] = useState('');
  const [startFrequency, setStartFrequency] = useState('day');
  const [time, setTime] = useState('00:00');
  const [minute, setMinute] = useState('00');
  const [timezone, setTimezone] = useState('US/Eastern');
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (referral_flow) => {
    setTimezone(referral_flow?.time_zone);
    setStartFrequency(referral_flow?.start_frequency ?? 'day');
    setDays(Object.fromEntries(Object.keys(weekdays).map(day => [day, referral_flow.start_weekdays.includes(day)])));
    if (referral_flow?.start_frequency === 'day') {
      setTime(referral_flow?.start_offset ? parseDuration(referral_flow?.start_offset) : '00:00');
    } else if (referral_flow?.start_frequency === 'hour') {
      setMinute(referral_flow?.start_offset ? parseDuration(referral_flow.start_offset, 'minute'): '00');
    }
  };
  const handleExited = () => {
    setTimezone('US/Eastern');
    setDays(weekdays);
    setStartFrequency('day');
    setTime('00:00');
    setMinute('00');
    setError('');
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;
    const selectedDays = Object.keys(days).filter(day => days[day]);

    if (selectedDays.length === 0) {
      setError(`Days can't be empty`);
      hasError = true;
    }

    if (hasError) return;

    let startOffset = '';
    if (startFrequency === 'hour') {
      startOffset = formatDuration(`00:${minute}`);
    } else if (startFrequency === 'day') {
      startOffset = formatDuration(time);
    }

    const referral_flow = {
      ...(needToUpdateField(referralFlow?.time_zone, timezone) && { time_zone: timezone }),
      ...(needToUpdateField(referralFlow?.start_weekdays, selectedDays) && { start_weekdays: selectedDays }),
      ...(needToUpdateField(referralFlow?.start_frequency, startFrequency) && { start_frequency: startFrequency }),
      ...(needToUpdateField(referralFlow?.start_offset, startOffset) && { start_offset: startOffset })
    };

    if (!_isEmpty(referral_flow)) {
      if (!!id) {
        updateReferralFlow(referral_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createReferralFlow(referral_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDayChange = (event) => {
    setDays({ ...days, [event.target.name]: event.target.checked });
    setError('');
  };
  const handleStartFrequency = (event) => {
    setStartFrequency(event.target.value);
  };
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_REFERRAL_FLOW });
  const fetchReferralAnalyses = useCallback(() => {
    getReferralAnalyses({ offset: 0 });
  }, [getReferralAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalReferralAnalyses();
    };
  }, [id, clearLocalReferralAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      setFirstTimeOpened(false);
      getReferralFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { referral_flow } = payload;
          handleEntering(referral_flow);
          fetchReferralAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getReferralFlow, fetchReferralAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              margin='dense'
              select
              label='Timezone'
              value={timezone}
              onChange={({ target: { value } }) => setTimezone(value)}
              disabled={fetching || (!!id && !edit)}
            >
              {timezones.map((tz) => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </TextField>
            <FormGroup row sx={{ justifyContent: 'center' }}>
              {Object.keys(days).map(day => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={days[day]}
                      onChange={handleDayChange}
                      name={day}
                      disabled={fetching || (!!id && !edit)}
                    />
                  }
                  label={day.charAt(0).toUpperCase() + day.slice(1)}
                />
              ))}
            </FormGroup>
            {error && <Typography align="center" color="error" fontSize="15px">{error}</Typography>}
            <Box sx={{ display: 'flex', width: '100%' }}>
              <TextField
                fullWidth
                margin="dense"
                id="startFrequency"
                disabled={fetching || (!!id && !edit)}
                sx={{ mr: '5px' }}
                label="Select type"
                select
                value={startFrequency}
                onChange={handleStartFrequency}
              >
                <MenuItem key={'day'} value="day">Every Day</MenuItem>
                <MenuItem key={'hour'} value="hour">Every Hour</MenuItem>
              </TextField>
              {startFrequency === 'day' ? (
                <TextField
                  fullWidth
                  margin="dense"
                  disabled={fetching || (!!id && !edit)}
                  label="Time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <TextField
                  fullWidth
                  margin="dense"
                  disabled={fetching || (!!id && !edit)}
                  label="Minute"
                  type="number"
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  InputProps={{ inputProps: { min: 0, max: 59 } }}
                />)}
            </Box>
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <ReferralAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ReferralFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);